import { useState, useEffect, Fragment } from "react";
import { cn } from "@/lib/utils";
import { MetaData, Step } from "@shared/types/message";
import { TextShimmer } from "@/components/ui/motion-primitives/text-shimmer";
import { ChevronDown, ChevronRight, Clock, AlertTriangle } from "lucide-react";
import {
    Timeline,
    TimelineItem,
    TimelineIndicator,
    TimelineSeparator,
    TimelineContent,
    TimelineTitle,
    TimelineHeader,
} from "@/components/ui/timeline";
import { useExtractTableFromLlmMessage } from "@/hooks/use-extract-table-from-llm-message";
import { useExtractMarkdownComponents } from "@/hooks/use-extract-markdown-components-from-llm-message";
import { useProcessElementsForChatRendering } from "@/hooks/use-process-elements-for-chat-rendering";
import { useTranslation } from "react-i18next";

type Props = {
    metadata?: MetaData;
    isStreaming?: boolean;
    threadId?: string;
};

// Component to render agent response with markdown support
const AgentResponseRenderer = ({ response, threadId }: { response: string; threadId?: string }) => {
    const { textParts, tables } = useExtractTableFromLlmMessage(response);
    const markdownComponents = useExtractMarkdownComponents("assistant", threadId);
    const processedElements = useProcessElementsForChatRendering(textParts, tables, markdownComponents);
    
    return <div className="text-sm leading-loose">{processedElements}</div>;
};

const AgentProcessingCard = ({ metadata, isStreaming = false, threadId }: Props) => {
    const { t } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [stuckSteps, setStuckSteps] = useState<Set<string>>(new Set());

    const startTime = metadata?.agentProcessing?.startTime;
    const endTime = metadata?.agentProcessing?.endTime;
    
    const TIMEOUT_THRESHOLD_MS = 60000; // 1 minute

    // Timer logic
    useEffect(() => {
        if (!startTime) return;

        // If processing is already complete, set the final elapsed time
        if (endTime) {
            setElapsedTime(endTime - startTime);
            return;
        }

        // Only start interval if still processing (no endTime)
        if (!endTime) {
            const interval = setInterval(() => {
                setElapsedTime(Date.now() - startTime);
            }, 100);

            return () => clearInterval(interval);
        }
    }, [startTime, endTime]);

    // Monitor steps for timeouts
    useEffect(() => {
        if (!isStreaming || !metadata?.agentProcessing?.steps) return;

        const checkInterval = setInterval(() => {
            const now = Date.now();
            const newStuckSteps = new Set<string>();

            metadata.agentProcessing.steps.forEach((step, index) => {
                // Only check the current active step (last step without endTime)
                const isLastStep = index === metadata.agentProcessing.steps.length - 1;
                const isActive = !step.endTime && isLastStep;
                
                if (isActive) {
                    const duration = now - step.startTime;
                    if (duration > TIMEOUT_THRESHOLD_MS) {
                        newStuckSteps.add(step.id);
                    }
                }
            });

            setStuckSteps(newStuckSteps);
        }, 1000); // Check every second

        return () => clearInterval(checkInterval);
    }, [isStreaming, metadata?.agentProcessing?.steps, TIMEOUT_THRESHOLD_MS]);

    if (!metadata?.agentProcessing) {
        return null;
    }

    const { steps } = metadata.agentProcessing;

    const formatElapsedTime = (ms: number) => {
        const seconds = Math.floor(ms / 1000);
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        const centiseconds = Math.floor((ms % 1000) / 10);

        if (minutes > 0) {
            return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}.${centiseconds.toString().padStart(2, "0")}`;
        }
        return `${remainingSeconds}.${centiseconds.toString().padStart(2, "0")}s`;
    };

    const getDuration = (start: number, end?: number) => {
        const endTimestamp = end || Date.now();
        const duration = endTimestamp - start;
        return formatElapsedTime(duration);
    };

    const getStepStatus = (step: Step, index: number) => {
        if (step.endTime) return "completed";
        if (isStreaming && index === steps.length - 1) {
            if (stuckSteps.has(step.id)) return "stuck";
            return "in-progress";
        }
        return "pending";
    };

    const displayCollapsedSelectionReason = !isExpanded && !metadata.agentProcessing.endTime && steps[steps.length - 1]

    const isProcessing = isStreaming && !endTime;
    const currentElapsed = endTime ? endTime - (startTime || 0) : elapsedTime;

    return (
        <div className="mb-3 rounded-lg border bg-transparent border-none text-card-foreground -mx-1 shadow-none">
            {/* Collapsed header */}
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex w-full items-center justify-between p-3 text-left transition-colors hover:bg-muted/50"
            >
                <div className="flex items-center space-x-2">
                    {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}

                    {isProcessing ? (
                        <TextShimmer className="text-sm font-medium">Thinking...</TextShimmer>
                    ) : (
                        <span className="text-sm font-medium text-muted-foreground">Thoughts</span>
                    )}
                </div>

                <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    <span className="font-mono">{formatElapsedTime(currentElapsed)}</span>
                </div>
            </button>

            {displayCollapsedSelectionReason && (
                <div className="px-3 pb-3">
                    <div className="pt-3 px-1">
                        <TextShimmer className="text-sm leading-loose">
                            {steps[steps.length - 1].selectionReason || 
                                `${steps[steps.length - 1].agentName} is working on the task...`}
                        </TextShimmer>
                    </div>
                </div>
            )}

            {/* Expanded content */}
            {isExpanded && (
                <div className="p-3 pt-5 pb-3">
                    <div>
                        <Timeline>
                            {steps.map((step, index) => {
                                const status = getStepStatus(step, index);

                                return (
                                    <Fragment key={index}>
                                        {/* Selection reasoning timeline item (comes first if it exists) */}
                                        {step.selectionReason && (
                                            <TimelineItem step={index * 2 + 1} className="pb-7">
                                                <TimelineHeader>
                                                    <TimelineSeparator />

                                                    <TimelineIndicator
                                                        className={cn(
                                                            "border-2 bg-background size-3",
                                                            status === "completed" && "border-green-500",
                                                            status === "in-progress" && "animate-pulse border-blue-500",
                                                            status === "pending" && "border-muted-foreground/30"
                                                        )}
                                                    />
                                                </TimelineHeader>
                                                <TimelineContent className="-mt-2.5 leading-loose">{step.selectionReason}</TimelineContent>
                                            </TimelineItem>
                                        )}

                                        {/* Agent response timeline item */}
                                        <TimelineItem step={index * 2 + 2} className="pb-7 last:pb-0">
                                            <TimelineHeader>
                                                <TimelineSeparator />
                                                <TimelineTitle className="flex items-center justify-between capitalize -mt-1">
                                                    <span className="flex items-center gap-2">
                                                        {step.agentName}
                                                        {status === "stuck" && (
                                                            <AlertTriangle className="h-4 w-4 text-orange-500 dark:text-orange-400" />
                                                        )}
                                                    </span>
                                                    {step.startTime && step.endTime && (
                                                        <span className="font-mono text-xs text-muted-foreground">
                                                            (
                                                            {getDuration(
                                                                steps[index - 1]
                                                                    ? steps[index - 1].startTime
                                                                    : step.startTime,
                                                                step.endTime
                                                            )}
                                                            )
                                                        </span>
                                                    )}
                                                </TimelineTitle>

                                                <TimelineIndicator
                                                    className={cn(
                                                        "border-2 bg-background size-3",
                                                        status === "completed" && "border-green-500 bg-green-500",
                                                        status === "in-progress" &&
                                                            "animate-pulse border-blue-500 bg-blue-500",
                                                        status === "stuck" &&
                                                            "animate-pulse border-orange-500 bg-orange-500",
                                                        status === "pending" && "border-muted-foreground/30"
                                                    )}
                                                />
                                            </TimelineHeader>
                                            <TimelineContent className="text-sm leading-loose">
                                                {step.agentResponse && (
                                                    <AgentResponseRenderer response={step.agentResponse} threadId={threadId} />
                                                )}

                                                {status === "stuck" && !step.agentResponse && (
                                                    <div className="rounded-md border-2 border-orange-500/50 bg-orange-50 p-3 dark:border-orange-500/30 dark:bg-orange-950/20">
                                                        <div className="flex items-start gap-2">
                                                            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-orange-600 dark:text-orange-400" />
                                                            <div>
                                                                <p className="text-sm font-medium text-orange-900 dark:text-orange-100">
                                                                    {t("agent.timeout.title", "Agent nimmt sich ungewöhnlich lange Zeit")}
                                                                </p>
                                                                <p className="mt-1 text-xs text-orange-800 dark:text-orange-200">
                                                                    {t("agent.timeout.description", "Dieser Schritt läuft bereits länger als 1 Minute. Der Agent arbeitet möglicherweise noch oder ist hängengeblieben.")}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {status === "in-progress" && !step.agentResponse && (
                                                    <p className="text-blue-600 dark:text-blue-400">
                                                        Processing...
                                                    </p>
                                                )}
                                            </TimelineContent>
                                        </TimelineItem>
                                    </Fragment>
                                );
                            })}
                        </Timeline>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AgentProcessingCard;
